import React, { useState, useEffect } from "react";
import { useTheme } from "../../context/ThemeContext";

interface DishForm5Props {
  onNext: (data: DishForm5Data) => void;
  onPrevious: () => void;
  onChange?: (data: Partial<DishForm5Data>) => void;
  initialData?: DishForm5Data;
}

export interface DishForm5Data {
  availableForDelivery: boolean;
  availableForDineIn: boolean;
  availableOnQRMenu: boolean;
  availabilitySchedule: string;
  availableDays: string[];
}

const DishForm5: React.FC<DishForm5Props> = ({ onNext, onPrevious, onChange, initialData }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState<DishForm5Data>({
    availableForDelivery: initialData?.availableForDelivery || false,
    availableForDineIn: initialData?.availableForDineIn || false,
    availableOnQRMenu: initialData?.availableOnQRMenu || false,
    availabilitySchedule: initialData?.availabilitySchedule || "",
    availableDays: initialData?.availableDays || [],
  });

  const [error, setError] = useState("");

  // Update form data when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      setFormData({
        availableForDelivery: initialData.availableForDelivery || false,
        availableForDineIn: initialData.availableForDineIn || false,
        availableOnQRMenu: initialData.availableOnQRMenu || false,
        availabilitySchedule: initialData.availabilitySchedule || "",
        availableDays: initialData.availableDays || [],
      });
    }
  }, [initialData]);

  const timeRangeOptions = [
    "All Day (24/7)",
    "Breakfast (6:00 AM - 11:00 AM)",
    "Lunch (11:00 AM - 3:00 PM)",
    "Snacks (3:00 PM - 6:00 PM)",
    "Dinner (6:00 PM - 11:00 PM)",
    "Custom"
  ];

  const dayOptions = [
    // "Monday",
    // "Tuesday",
    // "Wednesday",
    // "Thursday",
    // "Friday",
    // "Saturday",
    // "Sunday",
    "All Days",
    "Weekdays",
    "Weekends",
    "Custom"
  ];

  const handleToggle = (field: keyof DishForm5Data) => {
    const newValue = !formData[field];
    setFormData((prev) => ({
      ...prev,
      [field]: newValue,
    }));
    // Call onChange immediately to update parent state
    if (onChange) {
      onChange({ [field]: newValue } as Partial<DishForm5Data>);
    }
  };

  const handleScheduleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSchedule = e.target.value;
    console.log("check_target", e.target.value)
    setFormData((prev) => ({
      ...prev,
      availabilitySchedule: newSchedule,
    }));
    setError("");
    // Call onChange immediately to update parent state
    if (onChange) {
      onChange({ availabilitySchedule: newSchedule });
    }
  };

  const handleDaysChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setFormData((prev) => ({
      ...prev,
      availableDays: selectedOptions,
    }));
    setError("");
    // Call onChange immediately to update parent state
    if (onChange) {
      onChange({ availableDays: selectedOptions });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.availableForDelivery && !formData.availableForDineIn && !formData.availableOnQRMenu) {
      setError("Please enable at least one availability channel (Delivery, Dine-In, or QR Menu)");
      return;
    }
    // if (!formData.availabilitySchedule) {
    //   setError("Please select an availability schedule");
    //   return;
    // }
    // if (formData.availableDays.length === 0) {
    //   setError("Please select at least one available day");
    //   return;
    // }

    onNext(formData);
  };

  return (
    <div className={`max-w-2xl mx-auto rounded-lg p-4 sm:p-6 md:p-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
      <h2 className={`font-Urbanist text-lg sm:text-xl md:text-[24px] font-semibold mb-4 sm:mb-6 ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
        Channel Availability
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Available for Delivery */}
        <div className="flex items-center justify-between">
          <label className={`font-Urbanist text-[14px] font-medium ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
            Available for Delivery
          </label>
          <button
            type="button"
            onClick={() => handleToggle("availableForDelivery")}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              formData.availableForDelivery ? "bg-[#FF8A00]" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.availableForDelivery ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* Available for Dine-In */}
        <div className="flex items-center justify-between">
          <label className={`font-Urbanist text-[14px] font-medium ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
            Available for Dine-In
          </label>
          <button
            type="button"
            onClick={() => handleToggle("availableForDineIn")}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              formData.availableForDineIn ? "bg-[#FF8A00]" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.availableForDineIn ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* Available on QR Menu */}
        <div className="flex items-center justify-between">
          <label className={`font-Urbanist text-[14px] font-medium ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
            Available on QR Menu
          </label>
          <button
            type="button"
            onClick={() => handleToggle("availableOnQRMenu")}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              formData.availableOnQRMenu ? "bg-[#FF8A00]" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.availableOnQRMenu ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* Availability Schedule */}
        <div>
          <label className={`block font-Urbanist text-[14px] font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
            Availability Schedule
          </label>
          <select
            value={formData.availabilitySchedule}
            onChange={handleScheduleChange}
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors appearance-none ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-gray-300'
                : 'bg-white border-gray-300 text-gray-400'
            }`}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: "right 0.5rem center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "1.5em 1.5em",
              paddingRight: "2.5rem",
            }}
          >
            <option value="">Select time range</option>
            {timeRangeOptions.map((option) => (
              <option key={option} value={option} className={isDarkMode ? 'text-white bg-gray-700' : 'text-[#050505] bg-white'}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {/* Available Days */}
        <div>
          <label className={`block font-Urbanist text-[14px] font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
            Available Days
          </label>
          <select
            // multiple={true}
            value={formData.availableDays.join(",")}
            onChange={(e) => {
              const value = e.target.value;
              if (value) {
                setFormData((prev) => ({
                  ...prev,
                  availableDays: value.split(","),
                }));
              }
            }}
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors appearance-none ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-gray-300'
                : 'bg-white border-gray-300 text-gray-400'
            }`}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: "right 0.5rem center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "1.5em 1.5em",
              paddingRight: "2.5rem",
            }}
          >
            <option value="">Select days</option>
            {dayOptions.map((day) => (
              <option key={day} value={day} className={isDarkMode ? 'text-white bg-gray-700' : 'text-[#050505] bg-white'}>
                {day}
              </option>
            ))}
          </select>
        </div>

        {/* Error Message */}
        {error && (
          <div className="text-red-500 text-sm font-Urbanist">{error}</div>
        )}

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
          <button
            type="button"
            onClick={onPrevious}
            className={`w-full sm:flex-1 border-2 hover:border-gray-400 font-Urbanist text-sm sm:text-[16px] font-semibold py-3 sm:py-3.5 rounded-full transition-colors ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-[#050505]'
            }`}
          >
            Previous
          </button>
          <button
            type="submit"
            className="w-full sm:flex-1 bg-[#FF8A00] hover:bg-[#E67A00] text-white font-Urbanist text-sm sm:text-[16px] font-semibold py-3 sm:py-3.5 rounded-full transition-colors"
          >
            Next
          </button>
        </div>
      </form>
    </div>
  );
};

export default DishForm5;
